import Cookies from "js-cookie";

type OAuthClientTokens = {
  access_token?: string | undefined;
  refresh_token?: string | undefined;
  id_token?: string | null | undefined;
  expires_in?: number | null | undefined;
};

const DEFAULT_ACCESS_TOKEN_EXPIRE_WINDOW = 30 * 60 * 1000; // 30 min window before making network refresh call.

type ServerAction = "login" | "logout" | "user" | "refresh";

function createAuthStorage(prefix: string) {
  function createCookieHandler(name: string) {
    return {
      get: () => {
        // Delete potential parent domain cookie because i'm paranoid
        Cookies.remove(`${prefix}${name}`, { domain: ".givenwell.com" });
        Cookies.remove(`${prefix}${name}`, { domain: ".test.givenwell.com" });
        Cookies.remove(`${prefix}${name}`, { domain: ".demo.givenwell.com" });

        return Cookies.get(`${prefix}${name}`);
      },
      set: (value: string, attrs?: Cookies.CookieAttributes) => Cookies.set(`${prefix}${name}`, value, attrs),
      remove: () => Cookies.remove(`${prefix}${name}`, {}),
    };
  }

  return {
    accessToken: createCookieHandler(".at"),
    refreshToken: createCookieHandler(".rt"),
  };
}

export type CreateAuthOptions = {
  serverUrl: string;
  redirectUri: string;
  logoutUri: string;
  environmentName: string;
  applicationName: string;
};

export function createAuth(options: CreateAuthOptions) {
  const storagePrefix = `${options.environmentName}.${options.applicationName}`;
  const storage = createAuthStorage(storagePrefix);
  let isAuthenticated = !!storage.accessToken.get();

  let refreshStatus: "loading" | "idle" = "idle";
  let lastRefetchTime: number | undefined;

  function generateServerUrl(serverAction: ServerAction, queryParams?: Record<string, string>) {
    const query = new URLSearchParams(queryParams);
    const queryString = query.toString().length > 0 ? `?${query.toString()}` : "";
    const path = `/auth/${options.applicationName}/${serverAction}`;
    return `${options.serverUrl}${path}${queryString}`;
  }

  function redirectToLogin() {
    window.location.assign(`/sign-in?to=${encodeURIComponent(window.location.pathname + window.location.search)}`);
  }

  /**
   * Clear session data without sending you out of the app.
   */
  function reset() {
    sessionStorage.clear();
    storage.accessToken.remove();
    storage.refreshToken.remove();
  }

  /**
   * Logs the user out and clears all session data.
   */
  function logout() {
    reset();

    const queryParams = {
      post_logout_redirect_uri: options.logoutUri,
    };

    const fullUrl = generateServerUrl("logout", queryParams);
    window.location.assign(fullUrl);
  }

  function getIsRefreshRequired() {
    const refreshToken = storage.refreshToken.get();
    if (!refreshToken) {
      return false;
    }
    const timeWindow = DEFAULT_ACCESS_TOKEN_EXPIRE_WINDOW;
    if (lastRefetchTime === undefined || lastRefetchTime < Date.now() - timeWindow) {
      return true;
    }
    return false;
  }

  /**
   * Refreshes the access token if required.
   */
  async function renewAuthTokens(force = false) {
    if (!getIsRefreshRequired() && !force) {
      return true;
    }
    if (refreshStatus === "loading") {
      return true;
    }
    const refreshToken = storage.refreshToken.get();
    if (!refreshToken) {
      if (isAuthenticated) {
        reset();
        redirectToLogin();
      }
      return false;
    }
    refreshStatus = "loading";
    // This request automatically sets the cookies on success.
    try {
      const response = await fetch(generateServerUrl("refresh"), {
        method: "POST",
        headers: { "content-type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          refresh_token: refreshToken,
        }),
      });

      if (!response.ok) {
        reset();
        redirectToLogin();
        return false;
      }

      const data = (await response.json()) as OAuthClientTokens;
      saveAuthTokens(data);
      lastRefetchTime = Date.now();
      return true;
    } catch (e) {
      // Failed to refresh token.
      reset();
      redirectToLogin();
      return false;
    } finally {
      refreshStatus = "idle";
    }
  }

  function hasAccessToken() {
    return isAuthenticated;
  }

  /**
   * @returns Access token. If an assumed role access token is available, it will be returned.
   */
  async function getAccessToken() {
    while (storage.refreshToken.get() && !lastRefetchTime) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    const accessToken = storage.accessToken.get();
    return accessToken ?? "";
  }

  function getRefreshToken() {
    return storage.refreshToken.get() ?? null;
  }

  function saveAuthTokens(data: OAuthClientTokens) {
    if (data.access_token) {
      storage.accessToken.set(data.access_token, { expires: 30 });
    }
    if (data.refresh_token) {
      storage.refreshToken.set(data.refresh_token, { expires: 100 });
    }
    lastRefetchTime = Date.now();
    isAuthenticated = true;
  }

  setInterval(
    () => {
      renewAuthTokens();
    },
    1000 * 60 * 30,
  );

  window.addEventListener("focus", () => renewAuthTokens());

  renewAuthTokens();

  return {
    hasAccessToken,
    getAccessToken,
    getRefreshToken,
    renewAuthTokens,

    reset,
    logout,

    saveAuthTokens,
    redirectToLogin,
  };
}

export type Auth = ReturnType<typeof createAuth>;
